import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EMPTY_VALUE, formatDateTime } from './formatting';
import {
  getReportableEvent,
  getReportableEvents,
  markReportableEventReviewed,
  markReportableEventUnread,
  markReportableEventUnreviewed,
} from '../../../api/v2/reportable_events';
import { useReportableEventCounts } from './counts_context';

import DataRefreshBar from './data_refresh_bar';
import EventDrawer from './event_drawer';
import ExportButton from './export_button';
import Filters, { ALL_REVIEWS, ALL_TYPES, OUTSTANDING } from './filters';
import ReportTypeChip from './report_type_chip';
import SortableHeader from './sortable_header';

import styles from './index.module.css';

const COLUMNS = [
  { label: 'Report ID', sortKey: 'id' },
  { label: 'Site', sortKey: 'site' },
  { label: 'Subject ID', sortKey: 'subject_id' },
  { label: 'Report Date/Time', sortKey: 'reported_at' },
  { label: 'Report Type', sortKey: 'report_type' },
  { label: 'Keyword / Mentor Report' },
  { label: 'Source', sortKey: 'source' },
  { label: 'Site Notified', sortKey: 'site_notified_at' },
  { label: 'Latest Participant Status', sortKey: 'latest_participant_status' },
  { label: 'Reviewed' },
];

const DEFAULT_FILTERS = {
  siteIds: [],
  dateRange: 'all',
  reportType: ALL_TYPES,
  reviewStatus: ALL_REVIEWS,
  sortColumn: 'reported_at',
  sortDirection: 'desc',
};

const SORT_FOOTNOTES = {
  id: { asc: 'Ordered by report ID, lowest first', desc: 'Ordered by report ID, highest first' },
  site: { asc: 'Ordered by site name, A to Z', desc: 'Ordered by site name, Z to A' },
  subject_id: { asc: 'Ordered by subject ID, A to Z', desc: 'Ordered by subject ID, Z to A' },
  reported_at: { asc: 'Ordered oldest to newest', desc: 'Ordered newest to oldest' },
  report_type: { asc: 'Ordered by report type, A to Z', desc: 'Ordered by report type, Z to A' },
  source: { asc: 'Ordered by source, A to Z', desc: 'Ordered by source, Z to A' },
  site_notified_at: {
    asc: 'Ordered by site notification, oldest first, with events not yet notified last',
    desc: 'Ordered by site notification, newest first, with events not yet notified last',
  },
  latest_participant_status: {
    asc: 'Ordered by latest participant status, A to Z',
    desc: 'Ordered by latest participant status, Z to A',
  },
};

const isFiltered = (filters) =>
  filters.siteIds.length > 0 ||
  filters.dateRange !== DEFAULT_FILTERS.dateRange ||
  filters.reportType !== ALL_TYPES ||
  filters.reviewStatus !== ALL_REVIEWS;

const ReviewedCell = ({ event }) => {
  if (!event.reviewed) {
    return <span className={styles.outstanding}>Outstanding</span>;
  }

  return <span className={styles.reviewed}>Reviewed</span>;
};

const reportTypesFor = (event) => {
  if (event.report_types && event.report_types.length > 0) {
    return event.report_types;
  }

  return event.report_type ? [event.report_type] : [];
};

// The whole row opens the event, and the report ID is also a button so the row is reachable by
// keyboard rather than by mouse alone.
const EventRow = ({ event, onOpen }) => (
  <tr className={event.read ? styles.row : `${styles.row} ${styles.unread}`} onClick={() => onOpen(event.id)}>
    <td className={styles.report_id}>
      {!event.read && <span aria-label='Unread' className={styles.unread_dot} role='img' />}
      <button
        aria-label={`Open report ${event.id}`}
        className={styles.open_button}
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          onOpen(event.id);
        }}
        type='button'
      >
        {event.id}
      </button>
    </td>
    <td>
      <span className={styles.site_name}>{event.site_name || EMPTY_VALUE}</span>
      {event.site_id && <span className={styles.site_id}>{event.site_id}</span>}
    </td>
    <td className={styles.mono}>{event.subject_id || EMPTY_VALUE}</td>
    <td className={styles.nowrap}>{formatDateTime(event.reported_at)}</td>
    <td>
      <span className={styles.types}>
        {reportTypesFor(event).map((reportType) => (
          <ReportTypeChip key={reportType} reportType={reportType} />
        ))}
      </span>
    </td>
    <td>
      <span className={styles.snippet} title={event.keyword_or_mentor_report || undefined}>
        {event.keyword_or_mentor_report || EMPTY_VALUE}
      </span>
    </td>
    <td className={styles.nowrap}>{event.source}</td>
    <td className={styles.nowrap}>{formatDateTime(event.site_notified_at)}</td>
    <td>{event.latest_participant_status}</td>
    <td>
      <ReviewedCell event={event} />
    </td>
  </tr>
);

const EmptyAccount = () => (
  <div className={styles.empty}>
    <p className={styles.empty_title}>No reportable events</p>
    <p className={styles.empty_body}>
      Nothing has been flagged for the sites you are assigned to. New events appear here as they are detected.
    </p>
  </div>
);

// Separate from the empty-account state, so a reviewer who has filtered everything out is not told
// there is nothing to review.
const NoMatches = ({ onReset }) => (
  <div className={styles.empty}>
    <p className={styles.empty_title}>No reportable events match the current filters</p>
    <button className={styles.empty_action} onClick={onReset} type='button'>
      Clear filters
    </button>
  </div>
);

export default function ReportableEvents() {
  const { applyCounts } = useReportableEventCounts();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openEvent, setOpenEvent] = useState(null);
  const openEventIdRef = useRef(null);

  useEffect(() => {
    setLoading(true);

    const controller = new AbortController();
    let isCurrent = true;

    getReportableEvents(filters, { signal: controller.signal })
      .then((data) => {
        if (!isCurrent) return;
        setPayload(data);
        setError(null);
        applyCounts(data);
      })
      .catch((err) => {
        if (!isCurrent) return;
        if (err?.name === 'AbortError') return;
        setError('We could not load reportable events. Refresh the page to try again.');
      })
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [filters, applyCounts]);

  const applyFilters = useCallback((changes) => setFilters((current) => ({ ...current, ...changes })), []);

  // The list row is brought into line with what the server just said, rather than reloading the
  // whole list, so a filtered and sorted view is not rebuilt on every state change.
  const updateRow = useCallback(
    (id, changes) =>
      setPayload((current) => ({
        ...current,
        events: current.events.map((row) => (row.id === id ? { ...row, ...changes } : row)),
      })),
    []
  );

  // Opening an event is what marks it read.
  const openEventById = useCallback(
    (id) => {
      setOpenEvent({ id: id, loading: true, detail: null, error: null, saving: false, savingError: null });
      openEventIdRef.current = id;

      getReportableEvent(id)
        .then((data) => {
          setOpenEvent((current) => {
            if (!current || current.id !== id) {
              return current;
            }
            return { ...current, loading: false, detail: data.event };
          });
          updateRow(id, { read: true });
          applyCounts(data);
        })
        .catch(() =>
          setOpenEvent((current) => {
            if (!current || current.id !== id) {
              return current;
            }
            return { ...current, loading: false, error: 'We could not load this event.' };
          })
        );
    },
    [applyCounts, updateRow]
  );

  const closeEvent = useCallback(() => {
    setOpenEvent(null);
    openEventIdRef.current = null;
  }, []);

  const defaultDirectionFor = useCallback((sortKey) => {
    if (/_at$|_on$|date/i.test(sortKey)) {
      return 'desc';
    }
    return 'asc';
  }, []);

  const toggleReviewed = useCallback(
    (reviewed) => {
      const id = openEvent.id;
      const request = reviewed ? markReportableEventReviewed(id) : markReportableEventUnreviewed(id);

      setOpenEvent((current) => {
        if (!current || current.id !== id) {
          return current;
        }
        return { ...current, saving: true, savingError: null };
      });

      return request
        .then((summary) => {
          const changes = {
            read: summary.read,
            reviewed: summary.reviewed,
            reviewed_by: summary.reviewed_by,
            reviewed_at: summary.reviewed_at,
          };

          setOpenEvent((current) => {
            if (!current || current.id !== id) {
              return current;
            }
            const previousDetail = current.detail || {};
            return { ...current, saving: false, detail: { ...previousDetail, ...changes } };
          });
          if (reviewed && filters.reviewStatus === OUTSTANDING) {
            setPayload((current) => {
              if (!current) return current;
              const filteredEvents = (current.events || []).filter((row) => row.id !== id);
              const newInViewCount = Math.max(0, (current.events_in_view_count || 0) - 1);
              return { ...current, events: filteredEvents, events_in_view_count: newInViewCount };
            });
          } else {
            updateRow(id, changes);
          }
          applyCounts(summary);
        })
        .catch(() =>
          setOpenEvent((current) => {
            if (!current || current.id !== id) {
              return current;
            }
            return {
              ...current,
              saving: false,
              savingError: 'We could not save that. Try again.',
            };
          })
        );
    },
    [openEvent, applyCounts, updateRow, filters.reviewStatus]
  );

  // Marking an event unread is setting it aside to come back to, so the drawer closes rather than
  // leaving an event on screen that the list now says has not been opened.
  const markUnread = useCallback(() => {
    const id = openEvent.id;

    setOpenEvent((current) => {
      if (!current || current.id !== id) {
        return current;
      }
      return { ...current, saving: true, savingError: null };
    });

    return markReportableEventUnread(id)
      .then((summary) => {
        updateRow(id, { read: summary.read });
        applyCounts(summary);
        setOpenEvent(null);
        openEventIdRef.current = null;
      })
      .catch(() =>
        setOpenEvent((current) => {
          if (!current || current.id !== id) {
            return current;
          }
          return {
            ...current,
            saving: false,
            savingError: 'We could not save that. Try again.',
          };
        })
      );
  }, [openEvent, applyCounts, updateRow]);

  const sortBy = useCallback(
    (column) =>
      setFilters((current) => {
        if (current.sortColumn !== column.sortKey) {
          return { ...current, sortColumn: column.sortKey, sortDirection: defaultDirectionFor(column.sortKey) };
        }

        return { ...current, sortDirection: current.sortDirection === 'asc' ? 'desc' : 'asc' };
      }),
    [defaultDirectionFor]
  );

  // Sort is deliberately left alone: it is a separate decision from the filters, and resetting a
  // filter should not silently rearrange the rows.
  const resetFilters = useCallback(
    () =>
      setFilters((current) => ({
        ...DEFAULT_FILTERS,
        sortColumn: current.sortColumn,
        sortDirection: current.sortDirection,
      })),
    []
  );

  const events = useMemo(() => payload?.events || [], [payload]);
  const hasAnyEvents = (payload?.account_event_count || 0) > 0;
  const sortFootnote = SORT_FOOTNOTES[filters.sortColumn][filters.sortDirection];

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Reportable Events</h1>
          <p className={styles.subtitle}>
            Conversations flagged for review across the sites you are assigned to. Event records cannot be edited.
          </p>
        </div>

        <ExportButton disabled={!payload || Boolean(error) || loading} filters={filters} />
      </header>

      <DataRefreshBar dataRefresh={payload?.data_refresh} />

      {payload && hasAnyEvents && (
        <Filters
          eventCount={payload.filtered_event_count}
          filters={filters}
          isFiltered={isFiltered(filters)}
          onChange={applyFilters}
          onReset={resetFilters}
          outstandingCount={payload.outstanding_in_filter_count}
          sites={payload.sites}
          typeCounts={payload.type_counts}
        />
      )}

      {loading && !payload && <p className={styles.status}>Loading reportable events…</p>}
      {error && <p className={styles.error}>{error}</p>}

      {payload && !error && !hasAnyEvents && <EmptyAccount />}
      {payload && !error && hasAnyEvents && events.length === 0 && <NoMatches onReset={resetFilters} />}

      {payload && !error && events.length > 0 && (
        <>
          <p className={styles.in_view}>
            <span className={styles.in_view_count}>{payload.events_in_view_count}</span>
            reportable {payload.events_in_view_count === 1 ? 'event' : 'events'} in view
            {isFiltered(filters) && (
              <span className={styles.in_view_total}>of {payload.account_event_count} on this account</span>
            )}
          </p>

          <div className={loading ? `${styles.table_wrapper} ${styles.reloading}` : styles.table_wrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <SortableHeader
                      column={column}
                      key={column.label}
                      label={column.label}
                      onSort={sortBy}
                      sortColumn={filters.sortColumn}
                      sortDirection={filters.sortDirection}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <EventRow key={event.id} event={event} onOpen={openEventById} />
                ))}
              </tbody>
            </table>
          </div>

          <p className={styles.footnote}>
            {sortFootnote} · every column is sortable except Keyword / Mentor Report and Reviewed
          </p>
        </>
      )}

      {openEvent && (
        <EventDrawer
          error={openEvent.error}
          event={openEvent.detail}
          eventId={openEvent.id}
          loading={openEvent.loading}
          onClose={closeEvent}
          onMarkUnread={markUnread}
          onToggleReviewed={toggleReviewed}
          saving={openEvent.saving}
          savingError={openEvent.savingError}
        />
      )}
    </section>
  );
}
